import React, { useMemo, useRef, useState } from 'react';
import { Select, Space, Spin, Typography } from 'antd';
import { debounce } from 'lodash';
import axios from '../services/axiosInstance';
import { getImageUrl } from '../utils/imageUtils';

const { Text } = Typography;

const getMaterialId = (material) =>
  material?.material_id ?? material?.id ?? material?.materialId;

const MaterialAutocomplete = ({
  value,
  onChange,
  onSelect,
  onMaterialSelect,
  materials = [],
  placeholder = 'Search materials...',
  excludeSelected = [],
  style,
  disabled = false,
}) => {
  const [searchValue, setSearchValue] = useState(value || '');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const optionsRef = useRef([]);

  React.useEffect(() => {
    if (value !== undefined && value !== searchValue) {
      setSearchValue(value || '');
    }
    // Only sync when parent value prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const excludeSet = useMemo(
    () => new Set((excludeSelected || []).map((id) => String(id))),
    [excludeSelected]
  );

  const toOption = (material) => {
    const id = getMaterialId(material);
    const imagePath =
      material.material_image_path || material.image_path || material.imagePath;
    const meta = [
      material.code && `Code: ${material.code}`,
      material.color && `Color: ${material.color}`,
      material.type && `Type: ${material.type}`,
    ]
      .filter(Boolean)
      .join(' · ');

    return {
      value: String(id),
      material,
      label: (
        <Space align="start" size={8}>
          {imagePath ? (
            <img
              src={getImageUrl(imagePath)}
              alt=""
              style={{
                width: 36,
                height: 36,
                objectFit: 'cover',
                borderRadius: 4,
                flexShrink: 0,
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : null}
          <div style={{ minWidth: 0 }}>
            <div>
              <Text strong>{material.name}</Text>
            </div>
            {meta ? (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {meta}
              </Text>
            ) : null}
          </div>
        </Space>
      ),
    };
  };

  const runSearch = useMemo(
    () =>
      debounce(async (query) => {
        const q = (query || '').trim();
        if (q.length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }

        setLoading(true);
        try {
          let results = [];
          if (materials && materials.length > 0) {
            results = materials.filter(
              (material) =>
                (material.name || '')
                  .toLowerCase()
                  .includes(q.toLowerCase()) &&
                !excludeSet.has(String(getMaterialId(material)))
            );
          } else {
            const response = await axios.get(
              `/materials/search?q=${encodeURIComponent(q)}`
            );
            const data = Array.isArray(response.data)
              ? response.data
              : response.data?.data || [];
            results = data.filter(
              (material) => !excludeSet.has(String(getMaterialId(material)))
            );
          }
          const nextOptions = results.map(toOption);
          optionsRef.current = nextOptions;
          setOptions(nextOptions);
        } catch (error) {
          console.error('Error searching materials:', error);
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce recreates when filters change
    [materials, excludeSet]
  );

  const handleSearch = (query) => {
    setSearchValue(query);
    if (onChange) {
      onChange(query);
    }
    runSearch(query);
  };

  const clearSearch = () => {
    setSearchValue('');
    setOptions([]);
    if (onChange) {
      onChange('');
    }
  };

  const handleSelect = (selectedValue) => {
    const match =
      optionsRef.current.find((opt) => opt.value === String(selectedValue)) ||
      options.find((opt) => opt.value === String(selectedValue));
    if (match?.material) {
      (onSelect || onMaterialSelect)?.(match.material);
    }
    clearSearch();
  };

  return (
    <div data-testid="autocomplete-input">
      <Select
        showSearch
        filterOption={false}
        value={null}
        searchValue={searchValue}
        placeholder={placeholder}
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        notFoundContent={
          loading ? (
            <Spin size="small" />
          ) : searchValue.trim().length >= 2 ? (
            'No materials found'
          ) : (
            'Type at least 2 characters'
          )
        }
        disabled={disabled}
        style={{ width: '100%', ...style }}
        aria-label={placeholder}
        allowClear
        onClear={clearSearch}
        popupMatchSelectWidth
      />
    </div>
  );
};

export default MaterialAutocomplete;
